import * as React from 'react';
import { ThemeProvider, withStyles, WithStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Hidden from '@material-ui/core/Hidden';
import Navigator, { NavigatorSection, UnauthorizedNavigatorSection } from './Navigator';
import ConfigContentWrapper from './ConfigContentWrapper';

import Header from './Header';
import WelcomeContent from './WelcomeContent';
import FilesContent, { FileData, FileTabNames, TabGroup, TabGroupData, TabSuffix } from './FilesContent';
import { NavigatorData, Operations } from './NavigatorData';
import { Themes } from '../styling/Themes';
import { OperationContext, OperationContextInterface } from './OperationContext';
import DeltaResult, { EditorModel, ViewType } from '../DeltaResult'

import { Utilities } from '../Utilities';
import { CompareAction, CompareConfig } from '../api/compareAction';
import NotificationHandler from './NotificationHandler';
import { Link } from 'react-router-dom';
import LogContent, { currentTime } from './LogContent';

import Path from 'path';

import { DownloadAction } from '../api/downloadAction';
import FormDataType, { AncestorFile, AncestorFilename, ArrayAlignmentName, WordByWordName, ThreeWayMergeModeName, MergePriorityName, GraftResolutionModeName, PatchDirectionName, BaseConfiguration, InputFileA, InputFileB, InputFilenameAId, InputFilenameBId, ResultFilename, ResultFileState, JobDescriptor } from './ConfigFormData';
import { ErrorData } from './ConfigFormData';
import { ThreeWayMergeAction, ThreeWayMergeConfig } from '../api/threeWayMergeAction';
import { TwoWayMergeAction, TwoWayMergeConfig } from '../api/twoWayMergeAction';
import { GraftAction, GraftConfig } from '../api/graftAction';
import { CreatePatchConfig, CreatePatchAction } from '../api/patchAction';
import { CommonAction, RawJob, RawJobHistory } from '../api/commonAction';
import { JobsAction } from '../api/jobsAction';
import { AuthContext } from './AuthContext';
import SessionJobsWrapper, { JobsScope } from './SessionJobsWrapper';
import { AllJobs, JobData, JobsContentInfo, JobParametersW, JobsRequestStatus } from './JobsContent';
import { ThreeWayMergeMode } from '../api/commonConfig';
import { DeltaJSONColors } from '../styling/DeltaColors';
import { ProfileDetails } from './Profile';
import { CustomerSubscription, SubsData } from './SubscriptionData';

const downloadjs = require('downloadjs');

export enum PageType {
  Welcome,
  Configuration,
  Files,
  Logs,
  Level2,
  SessionJobs,
  JobsHistory,
}

let theme = Themes.MuiTheme;
const drawerWidth = 256;
const styles = Themes.createPaperBaseStyles(theme, drawerWidth);

export interface PaperbaseProps extends WithStyles<typeof styles> {
  headerType: PageType,
  notificationHandler: NotificationHandler,
}

export interface EventData {
  controlName: string,
  operationType: Operations,
  controlValue: string // either the text value or the filename
  controlFile: File | null
}

export type Severity = "error" | "success" | "info" | "warning" | undefined;

export type OperationLog = {
  type: Severity,
  title: string,
  message: string,
  time: string,
}


export interface TabGroupPayload {
  operationType: Operations,
  tabGroupData: TabGroupData
}

export interface TabGroupDataType {
  [Operations.compare]: TabGroupData;
  [Operations.threeWayMerge]: TabGroupData;
  [Operations.twoWayMerge]: TabGroupData;
  [Operations.graft]: TabGroupData;
  [Operations.patch]: TabGroupData;
}

export interface EditorModelType {
  [Operations.compare]: EditorModel[];
  [Operations.threeWayMerge]: EditorModel[];
  [Operations.twoWayMerge]: EditorModel[];
  [Operations.graft]: EditorModel[];
  [Operations.patch]: EditorModel[];
}

const initEditorModels: EditorModelType = {
  [Operations.compare]: [],
  [Operations.threeWayMerge]: [],
  [Operations.twoWayMerge]: [],
  [Operations.graft]: [],
  [Operations.patch]: []
}

export interface ProgressDataType {
  [Operations.compare]: ProgressData;
  [Operations.threeWayMerge]: ProgressData;
  [Operations.twoWayMerge]: ProgressData;
  [Operations.graft]: ProgressData;
  [Operations.patch]: ProgressData;
}

export interface ProgressData {
  percentage: number,
  logs: OperationLog[]
}

export interface FormUpdateData {
  formData: BaseConfiguration,
  operationType: Operations
}

export class AllJobsContentInfo {
  [Operations.compare]: JobsContentInfo = new JobsContentInfo();
  [Operations.threeWayMerge]: JobsContentInfo = new JobsContentInfo();
  [Operations.twoWayMerge]: JobsContentInfo = new JobsContentInfo();
  [Operations.graft]: JobsContentInfo = new JobsContentInfo();
  [Operations.patch]: JobsContentInfo = new JobsContentInfo();
}

export enum SubscriptionRequestStatus {
  noItems,
  notYetRequested,
  inProgress,
  resultOK,
  errorResponse
}

export type SubscriptionRequestType = { status: SubscriptionRequestStatus, statusNumber?: number, text?: string };

export class SubscriptionRequestData {
  public tiers: SubscriptionRequestType = { status: SubscriptionRequestStatus.notYetRequested };
  public subscriptions: SubscriptionRequestType = { status: SubscriptionRequestStatus.notYetRequested };
}

function combineTabGroups(operationType: Operations, allTabGroupData: TabGroupDataType, newTabGroupData: TabGroupData) {
  let updated: TabGroupDataType = { ...allTabGroupData, [operationType]: newTabGroupData };
  return updated;
}

function combineProgressData(operationType: Operations, allProgressData: ProgressDataType, newProgressData: ProgressData) {
  let updated: ProgressDataType = { ...allProgressData, [operationType]: newProgressData };
  return updated;
}

function combineEditorModels(operationType: Operations, allEditorModels: EditorModelType, newEditorModels: EditorModel[]) {
  let updated: EditorModelType = { ...allEditorModels, [operationType]: newEditorModels };
  return updated;
}

const initialFormValues = FormDataType.defaults;

const licensingApiURL = process.env.REACT_APP_LDS_SERVER_API_URL;
const deltajsonSaasProductName = "DeltaJSON SaaS";

const initProfileDetails: ProfileDetails = {
  emailAddress: "",
  avatarPicture: "",
}

function Paperbase(props: PaperbaseProps) {
  const [profileDetails, setProfileDetails] = React.useState(initProfileDetails);

  let server: string = process.env.REACT_APP_SERVER_API_URL!;
  let auth = React.useContext(AuthContext);

  const onSessionUpdate = () => {
    let emailAddress: string, avatarPicture: string;
    emailAddress = auth.authVariables?.email ? auth.authVariables?.email : "";
    avatarPicture = emailAddress.substring(0,2).toUpperCase();
    setProfileDetails(prevProfileDetails => ({
      ...prevProfileDetails,
      emailAddress: emailAddress,
      avatarPicture: avatarPicture
    }));

    if (auth.authVariables?.id_token) {
      const subRequestOptions: RequestInit = {
        method: 'GET',
        headers: {
          "Authorization": "Bearer " + auth.authVariables?.id_token
        }
      };
      getCustomerSubscriptionData(subRequestOptions);
    }
  };

  auth.onSessionUpdate = onSessionUpdate;

  const onConfigOperationChange = (operation: Operations) => {
    setConfigOperation(operation ? operation : Operations.compare);
  }

  const handleEditorUnMount = (model: EditorModel, id: FileTabNames) => {
    editorModels[id] = model;
    setAllEditorModels(combineEditorModels(operationContext.operationType, allEditorModels, editorModels));
  }

  const handleTabChange1 = (event: React.ChangeEvent<{}>, newValue: number, opType: Operations) => {
    setAllTabGroupData((prevAllTabGroupData) => {
      const temp = combineTabGroups(
        opType, prevAllTabGroupData,
        {
          ...prevAllTabGroupData[opType],
          tabValue1: newValue,
          activeTabGroup: TabGroup.group1
        }
      )
      return temp;
    });
  };

  const handleTabChange2 = (event: React.ChangeEvent<{}>, newValue: number, opType: Operations) => {
    setAllTabGroupData((prevAllTabGroupData) => {
      return combineTabGroups(
        opType, prevAllTabGroupData,
        {
          ...prevAllTabGroupData[opType],
          tabValue2: newValue,
          activeTabGroup: TabGroup.group2
        }
      )
    });
  };

  const onEditorDidGetFocus = (editorId: FileTabNames) => {
    const group = editorId === FileTabNames.ancestorInputTab || editorId === FileTabNames.inputATab || editorId === FileTabNames.rawOutTab ? TabGroup.group1 : TabGroup.group2;
    setAllTabGroupData(combineTabGroups(operationContext.operationType, allTabGroupData,
      {
        ...tabGroupData,
        activeTabGroup: group
      }));
  }

  let timoutId: number | undefined;

  // fix to allow non-blocking parse of JSON
  // using Response means that parsing happens on another thread
  function asyncParse(text: string): Promise<Response> {
    return (new Response(text)).json();
  }

  const handleEditorTextChange = (editorId: FileTabNames, text: string) => {
    // use timeout so that parse is only requested if user
    // has not typed for ${waitTime}

    const waitTime = 1000; // ms
    if (typeof timoutId === 'number') {
      window.clearTimeout(timoutId);
    }
    timoutId = window.setTimeout(() => {
      const newEditorModels = editorModels.map(item => item);
      let editorModelForIndex = newEditorModels[editorId];
      if (editorModelForIndex) {
        editorModelForIndex.isDirty = true;
        setAllEditorModels(combineEditorModels(operationContext.operationType, allEditorModels, newEditorModels));
      }
      asyncParse(text).then(() => {
        let fileInputIsValid = true;
        let graftChangesetIsValid = true;
        if (editorId === FileTabNames.inputATab) {
          if (operationContext.operationType === Operations.graft) {
            const dx_deltaJSON = (JSON.parse(text))['dx_deltaJSON'];
            if (!dx_deltaJSON || dx_deltaJSON.length === 0) {
              graftChangesetIsValid = false;
              fileInputIsValid = false;
            }
          }
          updateFormWithInputFileValidInfo('inputFileA', fileInputIsValid, formValues, graftChangesetIsValid);
        } else if (editorId === FileTabNames.inputBTab) {
          updateFormWithInputFileValidInfo('inputFileB', fileInputIsValid, formValues, graftChangesetIsValid);
        } else if (editorId === FileTabNames.ancestorInputTab) {
          updateFormWithInputFileValidInfo('ancestorFile', fileInputIsValid, formValues, graftChangesetIsValid);
        }
      })
        .catch(() => {
          const fileInputIsValid = false;
          if (editorId === FileTabNames.inputATab) {
            updateFormWithInputFileValidInfo('inputFileA', fileInputIsValid, formValues);
          } else if (editorId === FileTabNames.inputBTab) {
            updateFormWithInputFileValidInfo('inputFileB', fileInputIsValid, formValues);
          } else if (editorId === FileTabNames.ancestorInputTab) {
            updateFormWithInputFileValidInfo('ancestorFile', fileInputIsValid, formValues);
          }
        })
    }, waitTime);
  }

  const initTabGroupData: TabGroupDataType = {
    [Operations.compare]: {
      tabGroup1: [initFileGroup(Operations.compare, '1A.json')],
      tabGroup2: [initFileGroup(Operations.compare, '1B.json')],
      activeTabGroup: TabGroup.group1,
      tabValue1: 0,
      tabValue2: 0,
      onTabChange1: handleTabChange1,
      onTabChange2: handleTabChange2
    },
    [Operations.threeWayMerge]: {
      tabGroup1: [
        initFileGroup(Operations.threeWayMerge, 'Ancestor.json'),
        initFileGroup(Operations.threeWayMerge, '1A.json')
      ],
      tabGroup2: [initFileGroup(Operations.threeWayMerge, '1B.json')],
      activeTabGroup: TabGroup.group1,
      tabValue1: 0,
      tabValue2: 0,
      onTabChange1: handleTabChange1,
      onTabChange2: handleTabChange2
    },
    [Operations.twoWayMerge]: {
      tabGroup1: [initFileGroup(Operations.twoWayMerge, '1A.json')],
      tabGroup2: [initFileGroup(Operations.twoWayMerge, '1B.json')],
      activeTabGroup: TabGroup.group1,
      tabValue1: 0,
      tabValue2: 0,
      onTabChange1: handleTabChange1,
      onTabChange2: handleTabChange2
    },
    [Operations.graft]: {
      tabGroup1: [initFileGroup(Operations.graft, '1A.json')],
      tabGroup2: [initFileGroup(Operations.graft, '1B.json')],
      activeTabGroup: TabGroup.group1,
      tabValue1: 0,
      tabValue2: 0,
      onTabChange1: handleTabChange1,
      onTabChange2: handleTabChange2
    },
    [Operations.patch]: {
      tabGroup1: [initFileGroup(Operations.patch, '1A.json')],
      tabGroup2: [initFileGroup(Operations.patch, '1B.json')],
      activeTabGroup: TabGroup.group1,
      tabValue1: 0,
      tabValue2: 0,
      onTabChange1: handleTabChange1,
      onTabChange2: handleTabChange2
    },
  }
  const initProgressData = (): ProgressDataType => {
    return (
      {
        [Operations.compare]: { logs: [], percentage: 0 } as ProgressData,
        [Operations.threeWayMerge]: { logs: [], percentage: 0 } as ProgressData,
        [Operations.twoWayMerge]: { logs: [], percentage: 0 } as ProgressData,
        [Operations.graft]: { logs: [], percentage: 0 } as ProgressData,
        [Operations.patch]: { logs: [], percentage: 0 } as ProgressData,
      }
    )
  }

  const handleJobClick = async (job: JobData) => {
    let anySetJobInfo = allSetJobsContentInfo;
    if (job.resultContents === undefined) {
      setJobsRequestStatus(JobsRequestStatus.resultFetchInProgress);
      anySetJobInfo = setAllJobsHistoryContentInfo;
      let idToken: string | null = auth.authVariables?.id_token!;
      let hasError = false;
      const data: any = await JobsAction.getJobsData(server, job.jobId, idToken)
        .then((data) => data)
        .catch((err) => { hasError = true; return err });
      if (!hasError) {
        job.resultContents = data;
        setJobsRequestStatus(JobsRequestStatus.jobsOk);
      } else {
        job.error ? setJobsRequestStatus(JobsRequestStatus.errorInJobFetched) : setJobsRequestStatus(JobsRequestStatus.failedToLoadResult);
      }
    }
    anySetJobInfo((prevInfo) => {
      let allJobInfoCopy = { ...prevInfo };
      const jobInfo = { ...allJobInfoCopy[operationContext.operationType] };
      jobInfo.editorOpen = true;
      jobInfo.selectedJob = job;
      allJobInfoCopy[operationContext.operationType] = jobInfo;
      return allJobInfoCopy;
    });
  }

  const handleDrawerVisibilityWrapper = (jobsScope: JobsScope, close: boolean) => {
    const setJobsContentInfoFunction = jobsScope === JobsScope.History ? setAllJobsHistoryContentInfo : allSetJobsContentInfo;

    setJobsContentInfoFunction((prevInfo) => {
      let allJobInfoCopy = { ...prevInfo };
      const jobInfo = { ...allJobInfoCopy[operationContext.operationType] };
      if (jobInfo.selectedJob) {
        jobInfo.editorOpen = close;
      }
      allJobInfoCopy[operationContext.operationType] = jobInfo;
      return allJobInfoCopy;
    });
  };



  const { classes, headerType, notificationHandler } = props;
  const [configOperation, setConfigOperation] = React.useState(Operations.compare);
  const operationContext: OperationContextInterface = {
    operationType: configOperation,
    handleConfigOperationChange: onConfigOperationChange
  }
  const [allFormValues, setFormValues] = React.useState(initialFormValues);
  const updateFormValues = (formUpdateData: FormUpdateData) => {
    setFormValues((origFormValues) => {
      return { ...origFormValues, [formUpdateData.operationType]: formUpdateData.formData };
    });
  }

  const formValues: BaseConfiguration = allFormValues[operationContext.operationType];

  const [resultFilenames, setResultFilenames] = React.useState([] as string[]);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const [allEditorModels, setAllEditorModels] = React.useState(initEditorModels);
  const editorModels: EditorModel[] = allEditorModels[operationContext.operationType];

  const [allTabGroupData, setAllTabGroupData] = React.useState(initTabGroupData);
  const tabGroupData: TabGroupData = allTabGroupData[operationContext.operationType];

  const [allProgressData, setAllProgressData] = React.useState(initProgressData());

  const [allJobsData, setAllJobsData] = React.useState(new AllJobs());
  const [allJobsContentInfo, allSetJobsContentInfo] = React.useState(new AllJobsContentInfo());

  const [allJobsHistoryData, setAllJobsHistoryData] = React.useState(new AllJobs());
  const [allJobsHistoryContentInfo, setAllJobsHistoryContentInfo] = React.useState(new AllJobsContentInfo());

  const [jobsRequestStatus, setJobsRequestStatus] = React.useState(JobsRequestStatus.notYetRequested);

  const [subsData, setSubsData] = React.useState<CustomerSubscription>(SubsData.initCustomerSubscription);
  const [subscriptionResponseState, setSubscriptionResponseState] = React.useState(new SubscriptionRequestData());


  const updateFormWithInputFileValidInfo = (fileInputControl: string, fileInputIsValid: boolean, currentFormValues: BaseConfiguration, graftChangesetIsValid?: boolean) => {
    const previousResultFileExisted = formValues.ResultFileState !== ResultFileState.initial;
    const useNewFilename = !currentFormValues.isDirty && previousResultFileExisted;
    updateFormValues(
      {
        formData: {
          ...currentFormValues,
          isValid: isFormNowValid(fileInputControl, fileInputIsValid, currentFormValues.ErrorData),
          isDirty: true,
          ResultFilename: useNewFilename ? getNewResultFilename() : currentFormValues.ResultFilename,
          ErrorData: { ...currentFormValues.ErrorData, [fileInputControl]: fileInputIsValid ? null : (!graftChangesetIsValid) ? Utilities.invalidChangesetMessage : Utilities.invalidFileMessage },
          ResultFileState: currentFormValues.ResultFileState === ResultFileState.completedOk ? ResultFileState.completedOkButStale : currentFormValues.ResultFileState
        },
        operationType: operationContext.operationType
      });
  }

  function clearAllLogs() {
    setAllProgressData(initProgressData());
  }

  function clearLogForOperation(operation: Operations) {
    setAllProgressData(combineProgressData(operation, allProgressData, { logs: [], percentage: 0 }));
  }

  const actionProgressListener = (statusInfo: string, statusObject: any, operation: Operations) => {
    if (statusObject) {
      let infostatus: Severity = "info";
      if (compareStatusInfo !== statusInfo) {
        const newLogItem: OperationLog = { type: infostatus, title: "", message: JSON.stringify(statusObject, CompareAction.jobProgressProperties, pollingSettings.jsonIndent), time: currentTime() };
        const newPercentage = statusObject["progressInPercentage"];
        setAllProgressData((prevAllProgressData) => combineProgressData(operation, prevAllProgressData, { logs: [...prevAllProgressData[operationContext.operationType].logs, newLogItem], percentage: newPercentage }));
      }
      compareStatusInfo = statusInfo;
    }
  }

  let compareStatusInfo = "";


  let pollingSettings = {
    jsonIndent: 2,
    statusPollingInterval: 1000
  }

  const updateStateOnOperationError = (error: any, descriptor: string) => {
    updateFormValues({ formData: { ...formValues, isDirty: true }, operationType: operationContext.operationType });
    notificationHandler.notificationHandler(`${descriptor} Failed`, <Link to={NavigatorData.logsBaseUrl} style={{ color: DeltaJSONColors.WHITE }}>View Logs</Link>, "danger", 20000);
  }

  const runOperation = () => {

    let idToken: string | null = auth.authVariables?.id_token!;
    let ancestor: File | string | null = null;
    if (operationContext.operationType === Operations.threeWayMerge) {
      const tabEditorModel = editorModels[FileTabNames.ancestorInputTab];
      if (tabEditorModel && tabEditorModel.isDirty) {
        ancestor = tabEditorModel.text;
      } else {
        ancestor = formValues.AncestorFile!;
      }
    }

    let inputA: File | string | null = null;
    const tabEditorModel = editorModels[FileTabNames.inputATab];
    if (tabEditorModel && tabEditorModel.isDirty) {
      inputA = tabEditorModel.text;
    } else {
      inputA = formValues.InputFileA;
    }

    let inputB: File | string | null = null;
    const tabEditorModelB = editorModels[FileTabNames.inputBTab];
    if (tabEditorModelB && tabEditorModelB.isDirty) {
      inputB = tabEditorModelB.text;
    } else {
      inputB = formValues.InputFileB;
    }

    let config: CompareConfig | ThreeWayMergeConfig | TwoWayMergeConfig | GraftConfig | CreatePatchConfig;
    setFormValues((origFormValues) => {
      const newConfig: BaseConfiguration = { ...origFormValues[operationContext.operationType], isDirty: false, ResultFileState: ResultFileState.inProgress };
      return { ...origFormValues, [operationContext.operationType]: newConfig };
    });

    let commonAction: CommonAction;
    switch (operationContext.operationType) {
      case Operations.compare:
        commonAction = new CompareAction(server);
        config = new CompareConfig(inputA, inputB);
        config.setArrayAlignment(formValues.ArrayAlignmentOption);
        config.setWordByWord(formValues.WordByWordOption);
        break;
      case Operations.threeWayMerge:
        commonAction = new ThreeWayMergeAction(server);
        config = new ThreeWayMergeConfig(ancestor, inputA, inputB);
        config.setArrayAlignment(formValues.ArrayAlignmentOption);
        config.setWordByWord(formValues.WordByWordOption);
        config.setThreeWayMergeMode(formValues.ThreeWayMergeModeOption);
        break;
      case Operations.twoWayMerge:
        commonAction = new TwoWayMergeAction(server);
        config = new TwoWayMergeConfig(inputA, inputB);
        config.setArrayAlignment(formValues.ArrayAlignmentOption);
        config.setWordByWord(formValues.WordByWordOption);
        config.setMergePriority(formValues.MergePriorityOption);
        break;
      case Operations.graft:
        commonAction = new GraftAction(server);
        config = new GraftConfig(inputA, inputB);
        config.setGraftResolutionMode(formValues.GraftResolutionModeOption);
        break;
      case Operations.patch:
        commonAction = new CreatePatchAction(server);
        config = new CreatePatchConfig(inputA, inputB);
        config.setPatchDirection(formValues.PatchDirectionOption)
        break;
    }
    config.setjobLabel(formValues.JobDescriptor);
    config.setResultFilename(formValues.ResultFilename);

    const resultFilenameLogItem: OperationLog = { type: "info", title: `Starting Operation for result: ${formValues.ResultFilename}`, message: `operation #${resultFilenames.length + 1}`, time: currentTime() };
    setAllProgressData((prevAllProgressData) => combineProgressData(
      operationContext.operationType, prevAllProgressData, {
      logs: [...prevAllProgressData[operationContext.operationType].logs, resultFilenameLogItem],
      percentage: 1
    }));
    commonAction.addProgressListener(actionProgressListener).setPollingInterval(pollingSettings.statusPollingInterval);

    commonAction.action(config, idToken).then(([actionResult, jobReturnedAny]) => {
      const jobReturned = jobReturnedAny as RawJob;
      updateEditorModelsPostOperation(actionResult);
      setAllJobsData((prevAllJobsData) => {
        const allJobsDataCopy = { ...prevAllJobsData };
        const jobDataCopy: JobData[] = [...allJobsDataCopy[operationContext.operationType]];
        if (jobReturned) {
          const { timeCreate, jobStatus, comparisonTime } = jobReturned;
          const msecsNum: number = comparisonTime;
          const newJob: JobData = {
            jobId: jobReturned.jobId,
            jobLabel: formValues.JobDescriptor,
            jobStatus: jobStatus,
            timeCreate: timeCreate,
            jobDuration: msecsNum,
            resultFilename: formValues.ResultFilename,
            resultContents: actionResult,
            error: jobReturned.error,
            parameters: {
              inputA: formValues.InputFilenameA,
              inputB: formValues.InputFilenameB,
              ancestor: formValues.AncestorFilename,
              wordByWordOption: formValues.WordByWordOption,
              arrayAlignmentOption: formValues.ArrayAlignmentOption,
              mergePriorityOption: formValues.MergePriorityOption,
              graftResolutionModeOption: formValues.GraftResolutionModeOption,
              patchDirectionOption: formValues.PatchDirectionOption,
              threeWayMergeModeOption: formValues.ThreeWayMergeModeOption
            }
          };
          jobDataCopy.push(newJob);
        }
        allJobsDataCopy[operationContext.operationType] = jobDataCopy;
        return allJobsDataCopy;
      });
      setFormValues((origFormValues) => {
        const newResultFileState = origFormValues[operationContext.operationType].isDirty ? ResultFileState.completedOkButStale : ResultFileState.completedOk;
        const newConfig: BaseConfiguration = { ...origFormValues[operationContext.operationType], ResultFileState: newResultFileState, JobId: jobReturned.jobId };
        return { ...origFormValues, [operationContext.operationType]: newConfig };
      });
    })
      .catch((error: any) => {
        let errorstatus: Severity = "error";
        const msg = error.errorMessage + (error.errorCode ? ` [CODE: ${error.errorCode}]` : '');
        const newLogItem: OperationLog = { type: errorstatus, title: "Error", message: msg, time: currentTime() };

        setAllProgressData((prevAllProgressData) => combineProgressData(
          operationContext.operationType, prevAllProgressData, {
          logs: [...prevAllProgressData[operationContext.operationType].logs, newLogItem],
          percentage: prevAllProgressData[operationContext.operationType].percentage
        })
        );

        updateStateOnOperationError(error, operationContext.operationType);
        updateFormValues({ formData: { ...formValues, ResultFileState: ResultFileState.failedToComplete }, operationType: operationContext.operationType });
      });
  }


  const downloadResult = (jobData?: JobData) => {
    let downloadAction = new DownloadAction(server);
    // TODO: jobid should be managed in the formValues state but also passed as an argument in this callback
    const currentJobId = jobData?.jobId ? jobData.jobId : formValues.JobId;
    const currentResultFilename = jobData?.resultFilename ? jobData.resultFilename : formValues.ResultFilename;
    downloadAction.action(currentJobId, auth.authVariables?.id_token!).then((data) => {
      downloadjs(JSON.stringify(data), currentResultFilename, "application/json");
    });
  }

  const fileRegex = /^[\w\s&@,$=\-#().%+~]*.[A-Za-z0-9]+$/

  const whitespaceRegex = /^\s*$/

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const operationType = operationContext.operationType;

    let newControlValue: string;
    let newControlFile: File | null = null;
    if (event.target.files && event.target.files.length === 1) {
      newControlValue = event.target.files[0].name;
      newControlFile = event.target.files[0];
      // reset input element value so that file at same location can be uploaded for another operation
      event.target.value = '';
    } else {
      if (event.target.name === WordByWordName) {
        newControlValue = String(event.target.checked);
      } else {
        newControlValue = event.target.value;
      }
    }

    handleFormChangeData({
      controlName: event.target.name,
      operationType: operationType,
      controlValue: newControlValue,
      controlFile: newControlFile
    })
  };

  const handleFormChangeData = (eventData: EventData) => {
    const controlName = eventData.controlName;
    const newValueString = eventData.controlValue
    const { isDirty } = formValues;
    // TODO: fix issue with word-by-word and new result filename
    const previousResultFileExisted = formValues.ResultFileState !== ResultFileState.initial;
    const useNewFilename = !isDirty && previousResultFileExisted;

    let newResultFileState = formValues.ResultFileState === ResultFileState.completedOk ? ResultFileState.completedOkButStale : formValues.ResultFileState;
    switch (controlName) {
      case JobDescriptor:
        const newResultFilename = getNewResultFilename(newValueString + '.json');
        updateFormValues(
          {
            formData: {
              ...formValues,
              [controlName]: newValueString,
              ResultFilename: Utilities.convertDescriptorToFilename(newResultFilename),
              isDirty: true,
            },
            operationType: eventData.operationType
          });
        break;
      case ResultFilename:
        // don't change isDirty state:
        const resultFilename = newValueString;
        let { resultFilenameHasError, newResultFileErrorData } = validateResultFilename(resultFilename, whitespaceRegex, fileRegex, resultFilenames);
        const isResultnameValid = isFormNowValid('resultFile', !resultFilenameHasError, formValues.ErrorData);
        updateFormValues({
          formData: { ...formValues, [controlName]: newValueString, isValid: isResultnameValid, ErrorData: { ...formValues.ErrorData, resultFile: newResultFileErrorData } },
          operationType: eventData.operationType
        });
        break;
      case WordByWordName:
        updateFormValues(
          {
            formData: {
              ...formValues,
              [controlName]: (newValueString === 'true'),
              isDirty: true,
              ResultFilename: useNewFilename ? getNewResultFilename() : formValues.ResultFilename,
              ResultFileState: newResultFileState
            },
            operationType: eventData.operationType
          });
        break;
      case ThreeWayMergeModeName:
      case MergePriorityName:
      case PatchDirectionName:
      case GraftResolutionModeName:
      case ArrayAlignmentName:
        updateFormValues(
          {
            formData: {
              ...formValues,
              [controlName]: newValueString,
              isDirty: true,
              ResultFilename: useNewFilename ? getNewResultFilename() : formValues.ResultFilename,
              ResultFileState: newResultFileState
            },
            operationType: eventData.operationType
          });
        break;
      // TODO: update tab values also according to which input has been changed so that focus is shifted accordingly
      case InputFilenameAId:
      case InputFilenameBId:
      case AncestorFilename:
        const fileObj = eventData.controlFile ? eventData.controlFile : null;
        const filename = Utilities.getBaseFileName(newValueString);

        const newEditorModels = editorModels.map(item => item);
        let editorModel: EditorModel | null;
        let inputFileControl: string;
        if (controlName === InputFilenameAId) {
          editorModel = newEditorModels[FileTabNames.inputATab];
          inputFileControl = InputFileA;
        } else if (controlName === InputFilenameBId) {
          editorModel = newEditorModels[FileTabNames.inputBTab];
          inputFileControl = InputFileB;
        } else {
          editorModel = newEditorModels[FileTabNames.ancestorInputTab];
          inputFileControl = AncestorFile;
        }

        if (editorModel) {
          editorModel.isDirty = false;
        }

        setAllTabGroupData(combineTabGroups(operationContext.operationType, allTabGroupData,
          {
            ...tabGroupData,
            tabValue1: 0,
            tabValue2: 0
          }));

        setAllEditorModels(combineEditorModels(operationContext.operationType, allEditorModels, newEditorModels));

        let errorPropName: string;
        if (controlName === InputFilenameAId) {
          errorPropName = 'inputFileA';
        } else if (controlName === InputFilenameBId) {
          errorPropName = 'inputFileB';
        } else if (controlName === AncestorFilename) {
          errorPropName = 'ancestorFile';
        } else {
          errorPropName = 'resultFile'
        }

        let fileSizeLimitReached = false;
        if (fileObj) {
          Utilities.readFile(fileObj, (text) => {
            const newFormValues: BaseConfiguration = {
              ...formValues,
              [controlName]: filename,
              [inputFileControl]: fileObj,
              isDirty: true,
              ResultFilename: useNewFilename ? getNewResultFilename() : formValues.ResultFilename,
              ResultFileState: newResultFileState
            }

            const fileSizeLimitString = "5MB";
            const fileSizeLimit = (fileSizeLimitString) ? fileSizeLimitString : "";
            fileSizeLimitReached = Utilities.checkFileSizeLimit(fileObj, fileSizeLimit);

            updateFormValues(
              {
                formData: newFormValues,
                operationType: eventData.operationType
              });
            let formIsValid = false;
            asyncParse(text).then(() => {
              let inputErrorData = null;
              if (fileSizeLimitReached) {
                inputErrorData = Utilities.fileSizeLimitMessage;
              }
              if (controlName === InputFilenameAId) {
                if (operationContext.operationType === Operations.graft) {
                  const dx_deltaJSON = (JSON.parse(text))['dx_deltaJSON'];
                  if (!dx_deltaJSON || dx_deltaJSON.length === 0) {
                    inputErrorData = Utilities.invalidChangesetMessage;
                  }
                }
                formIsValid = isFormNowValid('inputFileA', inputErrorData === null, formValues.ErrorData);
              } else if (controlName === InputFilenameBId) {
                formIsValid = isFormNowValid('inputFileB', true, formValues.ErrorData);
              } else if (controlName === AncestorFilename) {
                formIsValid = isFormNowValid('ancestorFile', true, formValues.ErrorData);
              }
              updateFormValues({ formData: { ...newFormValues, isValid: formIsValid, ErrorData: { ...newFormValues.ErrorData, [errorPropName]: inputErrorData } }, operationType: operationContext.operationType });
            })
              .catch(() => {
                let inputErrorData = null;
                if (controlName === InputFilenameAId) {
                  inputErrorData = Utilities.invalidFileMessage;
                  formIsValid = isFormNowValid('inputFileA', false, formValues.ErrorData);
                } else if (controlName === InputFilenameBId) {
                  inputErrorData = Utilities.invalidFileMessage;
                  formIsValid = isFormNowValid('inputFileB', false, formValues.ErrorData);
                } else if (controlName === AncestorFilename) {
                  inputErrorData = Utilities.invalidFileMessage;
                  formIsValid = isFormNowValid('ancestorFile', false, formValues.ErrorData);
                } else if (fileSizeLimitReached) {
                  let currentFilename: string;
                  if (controlName === InputFilenameAId) {
                    currentFilename = 'inputFileA';
                  } else if (controlName === InputFilenameBId) {
                    currentFilename = 'inputFileB';
                  } else {
                    currentFilename = 'ancestorFile';
                  }
                  inputErrorData = Utilities.fileSizeLimitMessage;
                  formIsValid = isFormNowValid(currentFilename, false, formValues.ErrorData);
                }
                updateFormValues({ formData: { ...newFormValues, isValid: formIsValid, ErrorData: { ...newFormValues.ErrorData, [errorPropName]: inputErrorData } }, operationType: operationContext.operationType });
              })
          });
        }
        break;
    }
  };

  const initJobRequestStatus = (jobsScope: JobsScope) => {
    if (jobsScope === JobsScope.Session) {
      setJobsRequestStatus(JobsRequestStatus.jobsOk);
    }
  }

  const handleJobsHistoryEntered = async () => {
    let idToken: string | null = auth.authVariables?.id_token!;
    let hasError = false;
    setJobsRequestStatus(JobsRequestStatus.jobsFetchInProgress);
    const data: any = await JobsAction.getJobsData(server, null, idToken)
      .then((data) => data)
      .catch((err) => { hasError = true; return err });

    const newAllJobs = new AllJobs();
    if (hasError) {
      setAllJobsHistoryData(newAllJobs);
      setJobsRequestStatus(JobsRequestStatus.failedToFetch);
    } else {
      const jobsList: RawJobHistory[] = data.jobs;
      const sortedJobsList = jobsList.sort(Utilities.compareRawJob);
      sortedJobsList.forEach((job: RawJobHistory) => {

        const inputA = job.parameterSettings.inputs.a ?
          job.parameterSettings.inputs.a.size :
          job.parameterSettings.inputs.edit1 ?
            job.parameterSettings.inputs.edit1.size :
            job.parameterSettings.inputs.changeset ?
              job.parameterSettings.inputs.changeset.size :
              0;
        const inputB = job.parameterSettings.inputs.b ?
          job.parameterSettings.inputs.b.size :
          job.parameterSettings.inputs.edit2 ?
            job.parameterSettings.inputs.edit2.size :
            job.parameterSettings.inputs.target ?
              job.parameterSettings.inputs.target.size :
              0;

        const msecsNum: number =job.comparisonTime;
        const newParameters = new JobParametersW();
        newParameters.inputAKBytes = Utilities.getFileSizeInKB(inputA);
        newParameters.inputBKBytes = Utilities.getFileSizeInKB(inputB);
        newParameters.ancestorKBytes = job.parameterSettings.inputs.ancestor ? Utilities.getFileSizeInKB(job.parameterSettings.inputs.ancestor.size) : 0;
        newParameters.wordByWordOptionSet = job.parameterSettings.configuration.wordByWord;
        newParameters.arrayAlignmentOptionSet = job.parameterSettings.configuration.arrayAlignment;
        newParameters.mergePriorityOptionSet = job.parameterSettings.configuration.mergePriority;
        newParameters.graftResolutionModeOptionSet = job.parameterSettings.configuration.graftResolutionMode;
        newParameters.patchDirectionOptionSet = job.parameterSettings.configuration.patchDirection;
        newParameters.threeWayMergeModeOptionSet = job.parameterSettings.configuration.threeWayMergeMode;

        const newJobData: JobData = {
          jobId: job.jobId,
          jobLabel: job.jobDescriptor,
          jobStatus: job.jobStatus,
          timeCreate: job.timeCreate,
          jobDuration: msecsNum,
          resultFilename: job.resultFilename,
          error: job.error,
          parameters: newParameters
        };
        newAllJobs[NavigatorData.stringToOperation(job.operationType)].push(newJobData);
      });
      setAllJobsHistoryData(newAllJobs);
      setJobsRequestStatus(JobsRequestStatus.jobsOk);
    }
  }

  const handleFormDrop = (event: React.DragEvent<HTMLInputElement>, inputfilename: string) => {
    const operationType = operationContext.operationType;
    handleFormChangeData({
      controlName: inputfilename,
      operationType: operationType,
      controlValue: event.dataTransfer.files ? event.dataTransfer.files[0].name : "",
      controlFile: event.dataTransfer.files ? event.dataTransfer.files[0] : null
    })
  };

  function updateEditorModelsPostOperation(data: any) {
    setResultFilenames([...resultFilenames, formValues.ResultFilename]);
    const newEditorModels = editorModels.map(item => item);
    const tabEditorModelRaw = newEditorModels[FileTabNames.rawOutTab];
    if (tabEditorModelRaw) {
      tabEditorModelRaw.isDirty = false;
    }
    const tabEditorModelDiffOut = newEditorModels[FileTabNames.diffOutTab];
    if (tabEditorModelDiffOut) {
      tabEditorModelDiffOut.isDirty = false;
    }
    setAllEditorModels(combineEditorModels(operationContext.operationType, allEditorModels, newEditorModels));
    const { deltaObj } = DeltaResult.getDeltaObjectFromObject(data);
    const jsonString = JSON.stringify(deltaObj, null, 4);
    let tabInfo: string;
    let tabSuffix: TabSuffix = TabSuffix.None;
    switch (operationContext.operationType) {
      case Operations.compare:
        tabSuffix = TabSuffix.Raw;
        tabInfo = 'Compare: Raw DeltaJSON';
        break;
      case Operations.graft:
        tabSuffix = TabSuffix.Graft;
        tabInfo = operationContext.operationType + ' Result';
        break;
      case Operations.patch:
        tabSuffix = TabSuffix.Patch;
        tabInfo = operationContext.operationType + ' Result';
        break;
      case Operations.threeWayMerge:
        if (formValues.ThreeWayMergeModeOption === ThreeWayMergeMode.showConflicts) {
          tabSuffix = TabSuffix.Raw;
          tabInfo = 'Three Way Merge: Raw DeltaJSON';
        } else {
          tabSuffix = TabSuffix.ThreeWayMerge;
          tabInfo = 'Three Way Merge Result';
        }
        break;
      case Operations.twoWayMerge:
        tabSuffix = TabSuffix.TwoWayMerge;
        tabInfo = 'Two Way Merge Result';
        break;
    }
    const fileData1: FileData = {
      tabLabel: formValues.ResultFilename,
      tabSuffix: tabSuffix,
      viewType: ViewType.Raw,
      tabInfo: tabInfo,
      fileContent: jsonString
    }
    const existingFileGroup1ItemsCopy = allTabGroupData[operationContext.operationType].tabGroup1.slice();
    if (operationContext.operationType === Operations.threeWayMerge) {
      existingFileGroup1ItemsCopy[2] = fileData1;
    } else {
      existingFileGroup1ItemsCopy[1] = fileData1;
    }

    let existingFileGroup2ItemsCopy = allTabGroupData[operationContext.operationType].tabGroup2.slice();
    const populateSecondTabItem = operationContext.operationType === Operations.compare ||
      (operationContext.operationType === Operations.threeWayMerge && formValues.ThreeWayMergeModeOption === ThreeWayMergeMode.showConflicts);

    if (populateSecondTabItem) {
      const fileData2: FileData = {
        tabLabel: formValues.ResultFilename,
        tabSuffix: TabSuffix.Diff,
        viewType: ViewType.DiffResult,
        tabInfo: "DiffReport",
        fileContent: JSON.stringify(data)
      }
      existingFileGroup2ItemsCopy[1] = fileData2;
    } else if (existingFileGroup2ItemsCopy.length === 2) {
      existingFileGroup2ItemsCopy = [existingFileGroup2ItemsCopy[0]];
    }
    notificationHandler.notificationHandler(`${operationContext.operationType} Completed`, <Link to={NavigatorData.filesBaseUrl} style={{ color: DeltaJSONColors.WHITE }}>Please see Files for result</Link>, "success", 5000);

    setAllTabGroupData(combineTabGroups(operationContext.operationType, allTabGroupData,
      {
        ...tabGroupData,
        tabGroup1: existingFileGroup1ItemsCopy,
        tabGroup2: existingFileGroup2ItemsCopy,
        tabValue1: operationContext.operationType === Operations.threeWayMerge ? 2 : 1,
        tabValue2: populateSecondTabItem ? 1 : 0
      }));
  }

  async function updateFileData(inputFile: File | null | undefined, inputFilename: string | undefined, inputInfo: string): Promise<FileData> {
    const filename = inputFilename ? inputFilename : '';
    return new Promise((resolve) => {
      let newFileData: FileData = { tabLabel: filename, tabSuffix: TabSuffix.None, fileContent: '', tabInfo: inputInfo, viewType: ViewType.Raw };
      if (inputFile) {
        Utilities.readFile(inputFile, (text) => {
          newFileData = { ...newFileData, fileContent: text };
          resolve(newFileData);
        });
      } else {
        resolve(newFileData);
      }
    });
  }

  // Update Jobs data each time the state of a job changes
  React.useEffect(() => {

  }, [formValues, operationContext.operationType]);

  // TODO: update tabValue according to the input changed
  React.useEffect(() => {
    const label = operationContext.operationType === Operations.graft ? 'Changeset' : 'Input A';
    updateFileData(formValues.InputFileA, formValues.InputFilenameA, label).then((fileData) => {

      setAllTabGroupData((allTabGroupDataIn) => {
        const opTabGroupData = allTabGroupDataIn[operationContext.operationType];
        const indexInFileData = operationContext.operationType === Operations.threeWayMerge ? 1 : 0;
        const newFileData = opTabGroupData.tabGroup1.map((item, index) => index === indexInFileData ? fileData : item);
        return combineTabGroups(operationContext.operationType, allTabGroupDataIn,
          {
            ...opTabGroupData,
            tabGroup1: newFileData,
          }
        )
      });

    });
  }, [formValues.InputFileA, formValues.InputFilenameA, operationContext.operationType]);

  React.useEffect(() => {
    const label = operationContext.operationType === Operations.graft ? 'Target Input' : 'Input B';
    updateFileData(formValues.InputFileB, formValues.InputFilenameB, label).then((fileData) => {

      setAllTabGroupData((allTabGroupDataIn) => {
        const opTabGroupData = allTabGroupDataIn[operationContext.operationType];
        let newFileData: FileData[];
        if (opTabGroupData.tabGroup2.length === 2) {
          newFileData = [fileData, opTabGroupData.tabGroup2[1]];
        } else {
          newFileData = [fileData];
        }
        return combineTabGroups(operationContext.operationType, allTabGroupDataIn,
          {
            ...opTabGroupData,
            tabGroup2: newFileData,
          }
        )
      });

    });
  }, [formValues.InputFileB, formValues.InputFilenameB, operationContext.operationType]);

  React.useEffect(() => {
    if (operationContext.operationType !== Operations.threeWayMerge) {
      return;
    }
    updateFileData(formValues.AncestorFile, formValues.AncestorFilename, 'Input Ancestor').then((fileData) => {

      setAllTabGroupData((allTabGroupDataIn) => {
        const opTabGroupData = allTabGroupDataIn[operationContext.operationType];
        const indexInFileData = 0;
        const newFileData = opTabGroupData.tabGroup1.map((item, index) => index === indexInFileData ? fileData : item);
        return combineTabGroups(operationContext.operationType, allTabGroupDataIn,
          {
            ...opTabGroupData,
            tabGroup1: newFileData,
          }
        )
      });

    });
  }, [formValues.AncestorFile, formValues.AncestorFilename, operationContext.operationType]);


  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // const handleDemo1FileGroupLoad1 = (fileData: FileData[]) => {
  //   //setFileGroup1Items(fileData);
  // };

  // const handleDemo1FileGroupLoad2 = (fileData: FileData[]) => {
  //   // setFileGroup2Items(fileData);
  // };

  const handleClearInputs = () => {

    const fileObj = null;
    const inputErrorData = Utilities.emptyFile;

    const newEditorModels = editorModels.map(item => item);
    let editorModel: EditorModel | null;

    editorModel = newEditorModels[FileTabNames.inputATab];
    if (editorModel) {
      editorModel.isDirty = false;
    }
    editorModel = newEditorModels[FileTabNames.inputBTab];
    if (editorModel) {
      editorModel.isDirty = false;
    }
    editorModel = newEditorModels[FileTabNames.ancestorInputTab];
    if (editorModel) {
      editorModel.isDirty = false;
    }

    setAllTabGroupData(combineTabGroups(operationContext.operationType, allTabGroupData,
      {
        ...tabGroupData,
        tabValue1: 0,
        tabValue2: 0
      }));

    setAllEditorModels(combineEditorModels(operationContext.operationType, allEditorModels, newEditorModels));

    const previousResultFileExisted = formValues.ResultFileState !== ResultFileState.initial;
    const useNewFilename = !formValues.isDirty && previousResultFileExisted;
    const resultFilename = useNewFilename ? getNewResultFilename() : formValues.ResultFilename;
    const newResultFileState = !previousResultFileExisted ? ResultFileState.initial : ResultFileState.completedOkButStale;
    const baseFormData = {
      ...formValues,
      isDirty: true,
      isValid: false,
      InputFileA: fileObj,
      InputFileB: fileObj,
      ResultFilename: resultFilename,
      ResultFileState: newResultFileState,
      ErrorData: {
        ...formValues.ErrorData,
        inputFileA: inputErrorData,
        inputFileB: inputErrorData,
      }
    };

    if (operationContext.operationType === Operations.threeWayMerge) {
      // additionaly, set ancestor related properties
      updateFormValues({
        formData: {
          ...baseFormData,
          AncestorFile: fileObj,
          ErrorData: {
            ...baseFormData.ErrorData, ancestorFile: inputErrorData
          }
        },
        operationType: operationContext.operationType
      });
    } else {
      // don't set ancestor related properties
      updateFormValues({
        formData: baseFormData,
        operationType: operationContext.operationType
      });
    }

  }

  const suffixRegex = /-\d+$/

  function getNewResultFilename(filename?: string) {
    const fileNameSpecified = !!filename;
    const filenameBase = filename ? filename : formValues.ResultFilename;
    const ext = Path.extname(filenameBase);
    let nameWithoutExt = filenameBase.substring(0, filenameBase.length - ext.length);
    const suffixes = nameWithoutExt.match(suffixRegex);
    let fileSuffixNum = fileNameSpecified ? 0 : 2;
    let suffix = '';
    let suffixedName = ''
    if (suffixes) {
      suffix = suffixes[0];
      nameWithoutExt = nameWithoutExt.substring(0, nameWithoutExt.length - (suffix.length));
      fileSuffixNum = parseInt(suffix.substring(1));
      fileSuffixNum++;
    }
    const suffixString = fileSuffixNum === 0 ? '' : '-' + fileSuffixNum;
    suffixedName = nameWithoutExt + suffixString + ext;
    let attempts = 1;
    while (resultFilenames.indexOf(suffixedName) !== -1) {
      const newFileSuffixNum = Math.pow(10, attempts); // 1st attempt + 10, 2nd attempt 100 3rd attempt 1000
      suffixedName = nameWithoutExt + '-' + newFileSuffixNum + ext;
      attempts++;
    }

    return suffixedName;
  }

  let timer = 3000;

  const delayedSubscriptionDataCall = (count: number, subRequestOptions: RequestInit) => {
    setTimeout(() => {
      if (count > 0 ){
        delayedSubscriptionDataCall(count-1, subRequestOptions)
      } else {
        fetch(licensingApiURL + `/subscription/${auth.authVariables?.email}/${deltajsonSaasProductName}`, subRequestOptions)
          .then((response: Response) => {
            if (response.ok) {
              response.json()
                .then(result => {
                  const custSubData = SubsData.populateCustomerSubscriptionData(result);
                  if (custSubData) {
                    setSubsData(custSubData);
                  }
                  setSubscriptionResponseState(prevResponseState => ({ ...prevResponseState, subscriptions: { status: SubscriptionRequestStatus.resultOK } }));
                })
                .catch(error => console.error('getCustomerSubscriptionData error', error));
            } else if (response.status === 401) {
              setSubscriptionResponseState(prevResponseState => ({ ...prevResponseState, subscriptions: { status: SubscriptionRequestStatus.noItems } }));
            } else {
              setSubscriptionResponseState(prevResponseState => ({ ...prevResponseState, subscriptions: { status: SubscriptionRequestStatus.errorResponse, statusNumber: response.status } }));
            }
          }).catch(() => {
            setSubscriptionResponseState(prevResponseState => ({ ...prevResponseState, subscriptions: { status: SubscriptionRequestStatus.errorResponse, statusNumber: 0 } }));
          });
      }}, timer);
  }

  const getCustomerSubscriptionData = async (subRequestOptions: RequestInit) => {
    setSubscriptionResponseState(prevResponseState => ({ ...prevResponseState, subscriptions: { status: SubscriptionRequestStatus.inProgress } }));
    delayedSubscriptionDataCall(1, subRequestOptions);
  }

  const [monacoTheme, setMonacoTheme] = React.useState('light');

  const themeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    (event.target as HTMLInputElement).value === 'dark' ? setMonacoTheme('dark') : setMonacoTheme('light');
  }

  let conditionalParts: JSX.Element;
  let navSections: NavigatorSection[] = NavigatorData.RootSections;
  let unauthorizedSections: UnauthorizedNavigatorSection[] = NavigatorData.UnauthorizedSections;
  let isHome = true;

  if (auth.isAuthenticated() && subsData) {
    switch (headerType) {
      case PageType.Welcome:
        conditionalParts =
          <div className={classes.app}>
            <Header type={headerType} themeChange={themeChange} themeType={monacoTheme} 
              customerSubscriptionData={subsData} profileDetails={profileDetails}/>
            <main className={classes.main}>
              <WelcomeContent customerSubscriptionData={subsData} requestStatus={subscriptionResponseState}/>
            </main>
          </div>;
        break;
      case PageType.Configuration:
        conditionalParts =
          <div className={classes.app}>
            <Header type={headerType} themeChange={themeChange} themeType={monacoTheme}
              customerSubscriptionData={subsData} profileDetails={profileDetails}/>
            <main className={classes.main2}>
              <ConfigContentWrapper onFormDrop={handleFormDrop}
                onFormChange={handleFormChange}
                formValues={allFormValues}
                updateFormValues={updateFormValues}
                runOperation={runOperation}
                allProgressData={allProgressData}
                downloadResult={downloadResult}
                onClearInputs={handleClearInputs}
                customerSubscriptionData={subsData} />
            </main>
          </div>;
        break;
      case PageType.Files:
        conditionalParts =
          <div className={classes.app}>
            <main className={classes.main2}>
              <Header type={headerType} themeChange={themeChange} themeType={monacoTheme}
                customerSubscriptionData={subsData} profileDetails={profileDetails}/>
            </main>
            <FilesContent onFormDrop={handleFormDrop} onFormChange={handleFormChange} formValues={allFormValues} tabGroups={tabGroupData} onUnmount={handleEditorUnMount} onTextChange={handleEditorTextChange} editorModels={editorModels} onDidGetFocus={onEditorDidGetFocus} monacoTheme={monacoTheme} />
          </div>;
        break;
      case PageType.Logs:
        conditionalParts =
          <div className={classes.app}>
            <Header type={headerType} themeChange={themeChange} themeType={monacoTheme}
              customerSubscriptionData={subsData} profileDetails={profileDetails}/>
            <main className={classes.main}>
              <LogContent onClearAllLog={clearAllLogs} allProgressData={allProgressData} resultFileState={formValues.ResultFileState} onClearOperationLog={clearLogForOperation} />
            </main>
          </div>;
        break;
      case PageType.Level2:
        isHome = false;
        navSections = NavigatorData.CompareSections;
        conditionalParts =
          <div className={classes.app}>
            <Header type={headerType} themeChange={themeChange} themeType={monacoTheme}
              customerSubscriptionData={subsData} profileDetails={profileDetails}/>
            <main className={classes.main}>
              <WelcomeContent customerSubscriptionData={subsData} requestStatus={subscriptionResponseState}/>
            </main>
          </div>;
        break;
      case PageType.SessionJobs:
        conditionalParts =
          <div className={classes.app}>
            <Header type={headerType} themeChange={themeChange} themeType={monacoTheme}
              customerSubscriptionData={subsData} profileDetails={profileDetails}/>
            <main className={classes.main2}>
              <SessionJobsWrapper jobsData={allJobsData} jobInfo={allJobsContentInfo} handleJobClick={handleJobClick} handleDrawerVisibilityWrapper={handleDrawerVisibilityWrapper} jobsScope={JobsScope.Session} downloadResult={downloadResult}
                jobsRequestStatus={jobsRequestStatus} initJobRequestStatus={initJobRequestStatus} />
            </main>
          </div>
        break;
      case PageType.JobsHistory:
        conditionalParts =
          <div className={classes.app}>
            <Header type={headerType} themeChange={themeChange} themeType={monacoTheme}
              customerSubscriptionData={subsData} profileDetails={profileDetails}/>
            <main className={classes.main2}>
              <SessionJobsWrapper jobsData={allJobsHistoryData} jobInfo={allJobsHistoryContentInfo} jobsRequestStatus={jobsRequestStatus} initJobRequestStatus={initJobRequestStatus}
                handleJobClick={handleJobClick} handleDrawerVisibilityWrapper={handleDrawerVisibilityWrapper} handleJobsHistoryEntered={handleJobsHistoryEntered} jobsScope={JobsScope.History} downloadResult={downloadResult} />
            </main>
          </div>;
        break;
    }
  } else {
    conditionalParts =
      <div className={classes.app}>
        <Header type={PageType.Welcome} themeChange={themeChange} themeType={monacoTheme}
          customerSubscriptionData={subsData} profileDetails={profileDetails}/>
        <main className={classes.main}>
          <WelcomeContent customerSubscriptionData={subsData} requestStatus={subscriptionResponseState}/>
        </main>
      </div>;
  }

  return (
    <ThemeProvider theme={theme}>
      {window.addEventListener("dragover", (event: Event) => { event.preventDefault(); event.stopPropagation(); })}
      {window.addEventListener("drop", (event: Event) => { event.preventDefault(); event.stopPropagation(); })}
      <OperationContext.Provider value={operationContext}>
        <div className={classes.root}>
          <CssBaseline />
          <nav className={classes.drawer}>
            <Hidden smUp implementation="js">
              <Navigator
                PaperProps={{ style: { width: drawerWidth } }}
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                title={NavigatorData.title}
                ishome={isHome}
                sections={navSections}
                unauthorizedSections={unauthorizedSections}
                customerSubscriptionData={subsData}
                requestStatus={subscriptionResponseState}
              />
            </Hidden>
            <Hidden xsDown implementation="css">
              <Navigator
                PaperProps={{ style: { width: drawerWidth } }}
                title={NavigatorData.title}
                ishome={isHome}
                sections={navSections}
                unauthorizedSections={unauthorizedSections}
                customerSubscriptionData={subsData}
                requestStatus={subscriptionResponseState}
              />
            </Hidden>
          </nav>
          {conditionalParts}
        </div>
      </OperationContext.Provider>
    </ThemeProvider>
  );
}

export default withStyles(styles)(Paperbase);

function validateResultFilename(resultFilename: string, whitespaceRegex: RegExp, fileRegex: RegExp, resultFilenames: string[]) {
  let resultFilenameHasError = false;
  let newResultFileErrorData: string | null;
  if (resultFilename.length <= 0) {
    newResultFileErrorData = Utilities.emptyFile;
    resultFilenameHasError = true;
  } else if (resultFilename.match(whitespaceRegex)) {
    newResultFileErrorData = Utilities.whitespacesInFilename;
    resultFilenameHasError = true;
  } else if (resultFilename.match(fileRegex)) {
    if (resultFilenames.indexOf(resultFilename) === -1) {
      newResultFileErrorData = null;
      resultFilenameHasError = false;
    } else {
      newResultFileErrorData = Utilities.duplicateFilename;
      resultFilenameHasError = true;
    }
  } else {
    newResultFileErrorData = Utilities.invalidFilename;
    resultFilenameHasError = true;
  }
  return { resultFilenameHasError, newResultFileErrorData };
}

function initFileGroup(operationType: Operations, suffix: string): FileData {
  return (
    {
      tabLabel: `${NavigatorData.getSuffixForOperation(operationType)}${suffix}`,
      tabSuffix: TabSuffix.None,
      fileContent: '',
      tabInfo: 'Input File',
      viewType: ViewType.Raw
    }
  )
}

function isFormNowValid(currentFilename: string, currentFileIsValid: boolean, errorData: ErrorData) {
  const otherError = Object.entries(errorData).find((property) => {
    const [name, value] = property;
    return name !== currentFilename && value !== null;
  });
  let isFormValid: boolean;
  if (otherError) {
    isFormValid = false;
  } else {
    isFormValid = currentFileIsValid;
  }
  return isFormValid;
}
